import { personalityService } from './personality';

export class MicrophoneService {
  private stream: MediaStream | null = null;
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private microphone: MediaStreamAudioSourceNode | null = null;
  private isMonitoring = false;

  async checkMicrophone(): Promise<{ isWorking: boolean; message: string }> {
    try {
      // Solicita permissão do microfone
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Configura o contexto de áudio
      this.audioContext = new AudioContext();
      this.analyser = this.audioContext.createAnalyser();
      this.microphone = this.audioContext.createMediaStreamSource(this.stream);
      this.microphone.connect(this.analyser);

      // Configura o analisador
      this.analyser.fftSize = 256;
      const bufferLength = this.analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      // Monitora o volume por 2 segundos
      let maxVolume = 0;
      const startTime = Date.now();
      
      return new Promise((resolve) => {
        const checkVolume = () => {
          if (!this.analyser) return;
          
          this.analyser.getByteFrequencyData(dataArray);
          const currentVolume = Math.max(...Array.from(dataArray));
          maxVolume = Math.max(maxVolume, currentVolume);

          if (Date.now() - startTime < 2000) {
            requestAnimationFrame(checkVolume);
          } else {
            this.stopMonitoring();
            
            if (maxVolume > 50) {
              resolve({
                isWorking: true,
                message: personalityService.enhanceResponse(
                  "Microfone funcionando perfeitamente. Níveis de áudio ideais.",
                  'sucesso'
                )
              });
            } else {
              resolve({
                isWorking: false,
                message: personalityService.enhanceResponse(
                  "Microfone detectado, mas o volume está muito baixo. Verifique as configurações de entrada.",
                  'erro'
                )
              });
            }
          }
        };

        checkVolume();
      });
    } catch (error) {
      console.error('Erro ao acessar microfone:', error);
      return {
        isWorking: false,
        message: personalityService.enhanceResponse(
          "Não foi possível acessar o microfone. Verifique as permissões e conexões.",
          'erro'
        )
      };
    }
  }

  startMonitoring() {
    if (this.isMonitoring) return;
    this.isMonitoring = true;

    const monitorVolume = () => {
      if (!this.analyser || !this.isMonitoring) return;

      const bufferLength = this.analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      this.analyser.getByteFrequencyData(dataArray);

      const volume = Math.max(...Array.from(dataArray));
      
      // Emite evento com o volume atual
      window.dispatchEvent(new CustomEvent('microphone-volume', { 
        detail: { volume } 
      }));

      requestAnimationFrame(monitorVolume);
    };

    monitorVolume();
  }

  stopMonitoring() {
    this.isMonitoring = false;
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
    }
    if (this.audioContext) {
      this.audioContext.close();
    }
    this.stream = null;
    this.audioContext = null;
    this.analyser = null;
    this.microphone = null;
  }
}

export const microphoneService = new MicrophoneService();
